/**
 * Serializable column projection specs for AI query capability results.
 *
 * Plain data only (no render functions) so the same shape can be reused for
 * saved/refreshable tables later. The table renderer maps `kind` -> component.
 */

export type ColumnKind =
  | 'text'
  | 'date'
  | 'datetime'
  | 'status'
  | 'person'
  | 'personRef'
  | 'personRefs'
  | 'personRow'
  | 'workstreamRef'
  | 'workstream'
  | 'teamRef'
  | 'team'
  | 'service'
  | 'component'
  | 'boolean';

export type ColumnSpec =
  | {kind: 'text'; field: string; header: string}
  | {kind: 'date'; field: string; header: string}
  | {kind: 'datetime'; field: string; header: string}
  | {kind: 'status'; field: string; header: string}
  | {kind: 'boolean'; field: string; header: string}
  // Person reconstructed from flattened dotted scalar columns (combine_results
  // output), e.g. `owner.name` + `owner.ldap`.
  | {kind: 'person'; nameField: string; ldapField?: string; header: string}
  | {kind: 'personRef'; field: string; header: string}
  | {kind: 'personRefs'; field: string; header: string}
  | {kind: 'personRow'; header: string}
  | {kind: 'workstreamRef'; field: string; header: string}
  | {kind: 'workstream'; nameField: string; idField: string; header: string}
  | {kind: 'teamRef'; field: string; header: string}
  | {kind: 'team'; nameField: string; idField: string; slugField?: string; header: string}
  // DCD service linking to its Teams page: Apps for ServiceNow, the component's
  // service page for rSERVER (unlinked when it has no component).
  | {
      kind: 'service';
      nameField: string;
      idField: string;
      sourceField: string;
      componentIdField: string;
      header: string;
    }
  | {kind: 'component'; nameField: string; idField: string; header: string};

/** Optional columns appended only when at least one row has a non-null value. */
export type AppendWhenPopulatedField = {
  field: string;
  header: string;
  kind?: 'text' | 'date' | 'boolean';
};

/** A projection over result rows — safe to persist as JSON for replay later. */
export type TableProjection = {
  columns: ColumnSpec[];
  title?: string;
  /**
   * Rank measures, conditional profile fields, etc. — declared per capability so
   * CSV and saved live views match chat without hardcoding capability names in
   * getProjectionForCapability.
   */
  appendWhenPopulated?: AppendWhenPopulatedField[];
};

// Every count_* capability returns the same CountBucketDTO shape (a group label
// + a count, plus an optional entity ref for entity-keyed group_by).
const COUNT_BUCKET_PROJECTION: TableProjection = {
  title: 'Counts',
  columns: [
    {kind: 'text', field: 'group', header: 'Group'},
    {kind: 'text', field: 'count', header: 'Count'},
  ],
};

export const CAPABILITY_PROJECTIONS: Record<string, TableProjection> = {
  find_workstream_members: {
    title: 'Workstream members',
    columns: [
      {kind: 'personRef', field: 'person', header: 'Person'},
      {kind: 'workstreamRef', field: 'workstream', header: 'Workstream'},
      {kind: 'text', field: 'role', header: 'Role'},
      {kind: 'status', field: 'latest_status', header: 'Status'},
      {kind: 'date', field: 'last_update_at', header: 'Last update'},
    ],
  },
  find_workstreams: {
    title: 'Workstreams',
    columns: [
      {kind: 'workstream', nameField: 'name', idField: 'workstream_id', header: 'Workstream'},
      {kind: 'status', field: 'latest_status', header: 'Status'},
      {kind: 'text', field: 'pillar', header: 'Pillar'},
      {kind: 'personRefs', field: 'dris', header: 'DRIs'},
      {kind: 'date', field: 'last_update_at', header: 'Last update'},
    ],
    appendWhenPopulated: [
      {field: 'readiness', header: 'Readiness'},
      {field: 'checks_not_passing', header: 'Checks not passing'},
      {field: 'altitude', header: 'Altitude'},
      {field: 'metric_count', header: 'Metric count'},
      {field: 'member_count', header: 'Member count'},
    ],
  },
  count_workstreams: COUNT_BUCKET_PROJECTION,
  count_workstream_members: COUNT_BUCKET_PROJECTION,
  count_people: COUNT_BUCKET_PROJECTION,
  count_workstream_updates: COUNT_BUCKET_PROJECTION,
  count_outcomes: COUNT_BUCKET_PROJECTION,
  count_outcome_changes: COUNT_BUCKET_PROJECTION,
  count_news: COUNT_BUCKET_PROJECTION,
  count_gratitude: COUNT_BUCKET_PROJECTION,
  count_teams: COUNT_BUCKET_PROJECTION,
  find_workstream_updates: {
    title: 'Status updates',
    columns: [
      {kind: 'workstreamRef', field: 'workstream', header: 'Workstream'},
      {kind: 'personRef', field: 'author', header: 'Author'},
      {kind: 'status', field: 'status', header: 'Status'},
      {kind: 'datetime', field: 'created_at', header: 'Date'},
      {kind: 'text', field: 'comment', header: 'Update'},
    ],
  },
  find_outcomes: {
    title: 'Milestones',
    columns: [
      {kind: 'text', field: 'title', header: 'Title'},
      {kind: 'text', field: 'type', header: 'Type'},
      {kind: 'status', field: 'status', header: 'Status'},
      {kind: 'workstreamRef', field: 'workstream', header: 'Workstream'},
      {kind: 'personRef', field: 'owner', header: 'Owner'},
      {kind: 'text', field: 'current', header: 'Current'},
      {kind: 'text', field: 'target', header: 'Target'},
      {kind: 'date', field: 'complete_date', header: 'ETA'},
    ],
  },
  find_outcome_changes: {
    title: 'Milestone changes',
    columns: [
      {kind: 'workstreamRef', field: 'workstream', header: 'Workstream'},
      {kind: 'text', field: 'metric.title', header: 'Metric'},
      {kind: 'text', field: 'change_kind', header: 'Change'},
      {kind: 'personRef', field: 'changed_by', header: 'Changed by'},
      {kind: 'datetime', field: 'changed_at', header: 'When'},
    ],
  },
  get_workstream_detail: {
    title: 'Workstream detail',
    columns: [
      {kind: 'workstream', nameField: 'name', idField: 'workstream_id', header: 'Workstream'},
      {kind: 'status', field: 'latest_status', header: 'Status'},
      {kind: 'text', field: 'pillar', header: 'Pillar'},
      {kind: 'personRefs', field: 'dris', header: 'DRIs'},
      {kind: 'date', field: 'last_update_at', header: 'Last update'},
    ],
  },
  find_people: {
    title: 'People',
    columns: [
      {kind: 'personRow', header: 'Name'},
      {kind: 'text', field: 'role', header: 'Role'},
      {kind: 'text', field: 'level', header: 'Level'},
      {kind: 'text', field: 'team', header: 'Team'},
      {kind: 'text', field: 'location', header: 'Location'},
      {kind: 'text', field: 'manager_name', header: 'Manager'},
    ],
    appendWhenPopulated: [
      {field: 'is_deactivated', header: 'Former employee', kind: 'boolean'},
      {field: 'hire_date', header: 'Start date', kind: 'date'},
      {field: 'rehire_date', header: 'Rejoined', kind: 'date'},
      {field: 'badge_count', header: 'Badges'},
      {field: 'workstream_count', header: 'Workstreams'},
      {field: 'dri_workstream_count', header: 'DRI workstreams'},
      {field: 'languages', header: 'Languages'},
      {field: 'ergs', header: 'ERGs'},
      {field: 'enneagram', header: 'Enneagram'},
      {field: 'about_me', header: 'About me'},
      {field: 'birthday', header: 'Birthday'},
      {field: 'pto_start', header: 'PTO start', kind: 'date'},
      {field: 'pto_end', header: 'PTO end', kind: 'date'},
    ],
  },
  find_team_members: {
    title: 'Team members',
    columns: [
      {kind: 'personRef', field: 'person', header: 'Person'},
      {kind: 'teamRef', field: 'team', header: 'Team'},
      {kind: 'text', field: 'allocation', header: 'Allocation'},
    ],
  },
  count_team_members: COUNT_BUCKET_PROJECTION,
  get_my_profile: {
    title: 'Your profile',
    columns: [
      {kind: 'personRow', header: 'Name'},
      {kind: 'text', field: 'role', header: 'Role'},
      {kind: 'text', field: 'level', header: 'Level'},
      {kind: 'text', field: 'team', header: 'Team'},
      {kind: 'text', field: 'manager_name', header: 'Manager'},
      {kind: 'text', field: 'location', header: 'Location'},
    ],
    appendWhenPopulated: [
      {field: 'hire_date', header: 'Hire date', kind: 'date'},
      {field: 'rehire_date', header: 'Rejoined', kind: 'date'},
      {field: 'badge_count', header: 'Badges'},
      {field: 'workstream_count', header: 'Workstreams'},
      {field: 'dri_workstream_count', header: 'DRI workstreams'},
      {field: 'languages', header: 'Languages'},
      {field: 'ergs', header: 'ERGs'},
      {field: 'enneagram', header: 'Enneagram'},
      {field: 'about_me', header: 'About me'},
      {field: 'birthday', header: 'Birthday'},
      {field: 'pto_start', header: 'PTO start', kind: 'date'},
      {field: 'pto_end', header: 'PTO end', kind: 'date'},
    ],
  },
  find_teams: {
    title: 'Teams',
    columns: [
      {kind: 'team', nameField: 'name', idField: 'team_id', slugField: 'slug', header: 'Team'},
      {kind: 'text', field: 'parent_team_name', header: 'Parent'},
      {kind: 'text', field: 'member_count', header: 'Members'},
      {kind: 'personRef', field: 'primary_contact', header: 'Primary contact'},
    ],
  },
  get_my_teams: {
    title: 'Your teams',
    columns: [
      {kind: 'team', nameField: 'name', idField: 'team_id', slugField: 'slug', header: 'Team'},
      {kind: 'text', field: 'parent_team_name', header: 'Parent'},
      {kind: 'text', field: 'member_count', header: 'Members'},
    ],
  },
  get_my_org: {
    title: 'Your org',
    columns: [
      {kind: 'team', nameField: 'name', idField: 'team_id', slugField: 'slug', header: 'Team'},
      {kind: 'text', field: 'parent_team_name', header: 'Parent'},
      {kind: 'text', field: 'member_count', header: 'Members'},
      {kind: 'personRef', field: 'primary_contact', header: 'Primary contact'},
    ],
  },
  get_my_workstreams: {
    title: 'Your workstreams',
    columns: [
      {kind: 'workstream', nameField: 'name', idField: 'workstream_id', header: 'Workstream'},
      {kind: 'status', field: 'latest_status', header: 'Status'},
      {kind: 'text', field: 'pillar', header: 'Pillar'},
      {kind: 'text', field: 'role', header: 'Role'},
      {kind: 'date', field: 'last_update_at', header: 'Last update'},
    ],
  },
  find_news: {
    title: 'News',
    columns: [
      {kind: 'text', field: 'title', header: 'Title'},
      {kind: 'text', field: 'category_names', header: 'Categories'},
      {kind: 'text', field: 'body_preview', header: 'Preview'},
      {kind: 'datetime', field: 'created_at', header: 'Posted'},
    ],
  },
  find_gratitude: {
    title: 'Gratitude',
    columns: [
      {kind: 'personRef', field: 'sender', header: 'From'},
      {kind: 'personRef', field: 'recipient', header: 'To'},
      {kind: 'text', field: 'message', header: 'Message'},
      {kind: 'datetime', field: 'created_at', header: 'Date'},
    ],
  },
  find_badges: {
    title: 'Badges',
    columns: [
      {kind: 'text', field: 'name', header: 'Badge'},
      {kind: 'text', field: 'description', header: 'Description'},
    ],
  },
  find_badge_recipients: {
    title: 'Badge recipients',
    columns: [
      {kind: 'personRef', field: 'person', header: 'Person'},
      {kind: 'text', field: 'badge_name', header: 'Badge'},
      {kind: 'text', field: 'badge_description', header: 'Description'},
    ],
  },
  count_badge_recipients: COUNT_BUCKET_PROJECTION,
  find_workstream_teams: {
    title: 'Workstream teams',
    columns: [
      {kind: 'workstreamRef', field: 'workstream', header: 'Workstream'},
      {kind: 'teamRef', field: 'team', header: 'Team'},
      {kind: 'text', field: 'pillar', header: 'Pillar'},
    ],
  },
  count_workstream_teams: COUNT_BUCKET_PROJECTION,
  // DCD catalog rows: ids, reference counts, and search ranking stay off the table.
  find_services: {
    title: 'Services',
    columns: [
      {
        kind: 'service',
        nameField: 'name',
        idField: 'service_id',
        sourceField: 'source',
        componentIdField: 'component.component_id',
        header: 'Service',
      },
      {kind: 'text', field: 'source', header: 'Source'},
      {kind: 'text', field: 'owner_name', header: 'Owner'},
      {kind: 'text', field: 'system_owner_name', header: 'System owner'},
      {kind: 'personRefs', field: 'owners', header: 'People'},
      {
        kind: 'component',
        nameField: 'component.name',
        idField: 'component.component_id',
        header: 'Component',
      },
    ],
  },
  find_components: {
    title: 'Components',
    columns: [
      {kind: 'component', nameField: 'name', idField: 'component_id', header: 'Component'},
      {kind: 'text', field: 'owner_group_name', header: 'Owner group'},
      {kind: 'personRefs', field: 'owners', header: 'Owners'},
      {kind: 'text', field: 'slack_channel_public', header: 'Slack channel'},
      {kind: 'text', field: 'service_count', header: 'Services'},
    ],
  },
  find_service_references: {
    title: 'Service references',
    columns: [
      {kind: 'text', field: 'name', header: 'Reference'},
      {kind: 'text', field: 'reference_type', header: 'Type'},
      {kind: 'text', field: 'service.name', header: 'Service'},
      {kind: 'text', field: 'component.name', header: 'Component'},
    ],
  },
  find_component_owners: {
    title: 'Component owners',
    columns: [
      {kind: 'personRef', field: 'person', header: 'Person'},
      {kind: 'text', field: 'component_name', header: 'Component'},
    ],
  },
};

const PRIMITIVE_SKIP = new Set([
  'forecasts',
  'changes',
  'metrics',
  'recent_updates',
  'description',
  // Text-search ranking, for the model only.
  'match_score',
  'weak_match',
]);

/** Last dotted segment of a (possibly flattened) column name, lowercased. */
function fieldToken(field: string): string {
  const dot = field.lastIndexOf('.');
  return (dot >= 0 ? field.slice(dot + 1) : field).toLowerCase();
}

const isRefShape = (value: unknown, idKey: string): boolean =>
  typeof value === 'object' && value !== null && idKey in (value as Record<string, unknown>);

/**
 * Infer a column spec from a field name + a sample value, so datasets without an
 * explicit projection (joins, renames, aggregates) still render richly instead
 * of collapsing to plain text. Returns null for values we cannot render as a
 * cell (unknown nested objects / non-person lists), so the caller drops them.
 *
 * Native capability rows keep their nested ref shape (so `owner`/`workstream`/
 * `dris` are detected by shape); transform output is pre-flattened to dotted
 * scalars, so those resolve by name/value instead.
 */
export function classifyColumn(field: string, value: unknown): ColumnSpec | null {
  const header = humanizeField(field);

  if (Array.isArray(value)) {
    const first = value.find((entry) => entry != null);
    return isRefShape(first, 'user_id') ? {kind: 'personRefs', field, header} : null; // forecasts/changes/etc. — not a renderable cell
  }
  if (value !== null && typeof value === 'object') {
    if (isRefShape(value, 'user_id')) {
      return {kind: 'personRef', field, header};
    }
    if (isRefShape(value, 'workstream_id')) {
      return {kind: 'workstreamRef', field, header};
    }
    if (isRefShape(value, 'team_id')) {
      return {kind: 'teamRef', field, header};
    }
    return null; // unknown nested object — avoid rendering "[object Object]"
  }

  // Scalar / null: classify by value shape first (most reliable), then by name.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return {kind: 'datetime', field, header};
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return {kind: 'date', field, header};
  }
  if (typeof value === 'boolean') {
    return {kind: 'boolean', field, header};
  }

  const token = fieldToken(field);
  if (token === 'status' || token.endsWith('_status')) {
    return {kind: 'status', field, header};
  }
  if (token.endsWith('_at')) {
    return {kind: 'datetime', field, header};
  }
  if (token.endsWith('_date') || token === 'birthday' || token.startsWith('pto_')) {
    return {kind: 'date', field, header};
  }
  if (token.startsWith('is_') || token === 'complete' || token === 'blocked') {
    return {kind: 'boolean', field, header};
  }
  return {kind: 'text', field, header};
}

/** First non-null value seen for `key` across rows (preserves type when row 0 is null). */
function firstNonNull(items: Record<string, unknown>[], key: string): unknown {
  for (const item of items) {
    const value = item[key];
    if (value != null) {
      return value;
    }
  }
  return null;
}

/**
 * Reconstruct a rich ref column from a flattened dotted-prefix group, or null if
 * the group isn't a recognizable person/workstream. `combine_results` flattens
 * nested refs into dotted scalars (`workstream.name`, `owner.ldap`, ...); this
 * merges those back into a single linkable column. `consumes` lists the dotted
 * keys folded into the ref so the caller drops their standalone columns.
 */
function refColumnForPrefix(
  prefix: string,
  leaves: Set<string>
): {spec: ColumnSpec; consumes: string[]} | null {
  const full = (leaf: string) => `${prefix}.${leaf}`;
  const header = humanizeField(prefix);

  if (leaves.has('workstream_id')) {
    const hasName = leaves.has('name');
    return {
      spec: {
        kind: 'workstream',
        idField: full('workstream_id'),
        nameField: hasName ? full('name') : full('workstream_id'),
        header,
      },
      consumes: hasName ? [full('workstream_id'), full('name')] : [full('workstream_id')],
    };
  }

  if (leaves.has('team_id')) {
    const hasName = leaves.has('name');
    const hasSlug = leaves.has('slug');
    const consumes = [full('team_id')];
    if (hasName) {
      consumes.push(full('name'));
    }
    if (hasSlug) {
      consumes.push(full('slug'));
    }
    return {
      spec: {
        kind: 'team',
        idField: full('team_id'),
        nameField: hasName ? full('name') : full('team_id'),
        slugField: hasSlug ? full('slug') : undefined,
        header,
      },
      consumes,
    };
  }

  if (leaves.has('name') && (leaves.has('ldap') || leaves.has('user_id'))) {
    const consumes = [full('name')];
    for (const leaf of ['ldap', 'user_id', 'email']) {
      if (leaves.has(leaf)) {
        consumes.push(full(leaf));
      }
    }
    return {
      spec: {
        kind: 'person',
        nameField: full('name'),
        ldapField: leaves.has('ldap') ? full('ldap') : undefined,
        header,
      },
      consumes,
    };
  }

  return null;
}

/**
 * Fallback projection when a capability has no explicit spec. Unions column keys
 * across rows (left joins leave nulls on row 0) and classifies each by name +
 * value so joined/renamed/aggregated datasets keep status chips, person and
 * workstream links, and date formatting. Flattened dotted ref groups from
 * `combine_results` are recombined into single rich person/workstream columns.
 */
export function inferProjectionFromItems(items: Record<string, unknown>[]): TableProjection {
  if (items.length === 0) {
    return {columns: []};
  }
  const orderedKeys: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    for (const key of Object.keys(item)) {
      if (!seen.has(key)) {
        seen.add(key);
        orderedKeys.push(key);
      }
    }
  }

  // Group dotted keys by their first segment so flattened refs can be rebuilt.
  const leavesByPrefix = new Map<string, Set<string>>();
  for (const key of orderedKeys) {
    const dot = key.indexOf('.');
    if (dot > 0) {
      const prefix = key.slice(0, dot);
      const leaf = key.slice(dot + 1);
      const set = leavesByPrefix.get(prefix) ?? new Set<string>();
      set.add(leaf);
      leavesByPrefix.set(prefix, set);
    }
  }

  const columns: ColumnSpec[] = [];
  const consumed = new Set<string>();
  // Flat people rows (a find_people result after select_columns) carry the
  // person as top-level `name` + `ldap`; render the name as a profile link.
  const isPeopleRow = seen.has('name') && seen.has('ldap');
  for (const key of orderedKeys) {
    if (PRIMITIVE_SKIP.has(key) || consumed.has(key)) {
      continue;
    }

    if (isPeopleRow && key === 'name') {
      columns.push({kind: 'person', nameField: 'name', ldapField: 'ldap', header: 'Name'});
      consumed.add('ldap');
      consumed.add('user_id');
      continue;
    }

    const dot = key.indexOf('.');
    if (dot > 0) {
      const prefix = key.slice(0, dot);
      const ref = refColumnForPrefix(prefix, leavesByPrefix.get(prefix) ?? new Set());
      if (ref && ref.consumes.includes(key)) {
        columns.push(ref.spec);
        ref.consumes.forEach((c) => consumed.add(c));
        continue;
      }
    }

    const spec = classifyColumn(key, firstNonNull(items, key));
    if (spec) {
      columns.push(spec);
    }
  }
  return {columns};
}

/** Append optional columns when at least one row has a non-null value for that field. */
function appendPopulatedColumns(
  base: TableProjection,
  items: Record<string, unknown>[]
): TableProjection {
  const optional = base.appendWhenPopulated ?? [];
  const {appendWhenPopulated, ...rest} = base;
  void appendWhenPopulated;
  const extra = optional
    .filter(({field}) => firstNonNull(items, field) != null)
    .map(({field, header, kind}): ColumnSpec => {
      if (kind === 'date' || kind === 'boolean') {
        return {kind, field, header};
      }
      return {kind: 'text', field, header};
    });
  if (extra.length === 0) {
    return rest;
  }
  return {...rest, columns: [...base.columns, ...extra]};
}

export function getProjectionForCapability(
  capability: string,
  items: Record<string, unknown>[]
): TableProjection {
  const known = CAPABILITY_PROJECTIONS[capability];
  return known ? appendPopulatedColumns(known, items) : inferProjectionFromItems(items);
}

/** `foo_bar` / `workstream.name` -> `Foo Bar` / `Workstream Name`. */
function humanizeField(field: string): string {
  return field.replace(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
